import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Deep links to a section (e.g. a table-of-contents anchor) should land
    // on that section, not get yanked back to the top of the page.
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    // Add local business JSON-LD schema
    const existingScript = document.querySelector('script[data-schema="local-business"]');
    if (existingScript) {
      existingScript.remove();
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://fretdev.com',
      name: 'FretDev',
      description: 'Enterprise-grade AI and cloud engineering services in the Dallas-Fort Worth area',
      image: 'https://fretdev.com/logo.png',
      url: 'https://fretdev.com',
      email: 'info@fretdev.com',
      telephone: '+1-787-422-7032',
      priceRange: '$$$',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Carrollton',
        addressRegion: 'TX',
        postalCode: '75007',
        addressCountry: 'US',
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        description: 'Available by appointment',
      },
      areaServed: [
        {
          '@type': 'City',
          name: 'Dallas',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Carrollton',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Arlington',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Irving',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Plano',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Frisco',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Fort Worth',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'McKinney',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Richardson',
          state: 'Texas',
        },
        {
          '@type': 'City',
          name: 'Farmers Branch',
          state: 'Texas',
        },
      ],
      serviceType: [
        'Cloud Architecture',
        'Generative AI Development',
        'Machine Learning Solutions',
        'AWS Solutions Architecture',
        'Enterprise Cloud Engineering',
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61576176970099',
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'local-business');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="local-business"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#fafafa' }}>
      <Header />
      <div style={{ height: '88px', flexShrink: 0 }} />
      <main style={{ flex: 1, paddingBottom: '64px' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
